<template>
    <b-container
        class="listing-table vld-parent"
        :class="containerClass"
        fluid
    >
        <div
                class="multiselect-actions-wrapper d-flex align-items-center mb-1"
                v-if="hasMultiSelect && multiselectModel.length"
        >
            <span>
                {{ multiselectModel.length }} item{{ multiselectModel.length === 1 ? '' : 's' }} selected
            </span>

            <b-btn
                    class="multiselect-cancel"
                    variant="transparent"
                    @click="clearMultiSelect"
            >
                Cancel
            </b-btn>

            <div class="d-inline-block ml-auto multiselect-actions">
                <b-btn
                        v-for="(action,index) in validMultiSelectActions"
                        :key="index"
                        class="multiselect-action px-1 ml-2"
                        :disabled="
                            action.hasOwnProperty('isActionDisabled') ?
                            action.isActionDisabled(multiselectModel) : false
                        "
                        :hidden="
                            action.hasOwnProperty('isActionHidden') ?
                            action.isActionHidden(multiselectModel) : false
                        "
                        variant="transparent"
                        @click="handleMultiSelectActionClick(action)"
                >
                    <i
                            v-if="action.icon"
                            :class="action.icon"
                            class="multiselect-icon far mr-2"
                    />
                    <span class="multiselect-action-text">
                        {{ action.text }}
                    </span>
                </b-btn>
            </div>
        </div>

        <loading-resource-listing-table
                v-if="isLoading"
                :page-size="!disabledMinHeight ? pageSize : 1"
                :cells="cells"
                :table-row-class="loadingTableRowClass"
                :table-header-class="loadingTableHeaderClass"
                :table-header-cell-class="tableHeaderCellClass"
        />

        <template v-else>
            <div class="table-listing-rows-wrapper">
                <b-row
                        class="border-bottom d-flex table-header"
                        align-v="end"
                >
                    <template v-for="(cell, index) in cells">
                        <b-col
                                :class="tableHeaderCellClass + ' ' + cellClass(cell,'inline-flex')"
                                v-if="cell.header"
                                :key="cell.header ? cell.header : `header-${index}`"
                                :cols="cell.headerColSize ? cell.headerColSize : cellColSm(cell)"
                                class="cell"
                                :sm="cellColSm(cell)"
                                :md="cellColMd(cell)"
                                :lg="cellColLg(cell)"
                        >
                            <template v-if="hasMultiSelect && !isLoading && !index">
                                <b-form-checkbox
                                        class="ms-multiselect ms-multiselect-checkbox ms-multiselect-checkbox-all"
                                        @change="onMultiselectAll"
                                        :indeterminate="indeterminate"
                                        @click.native.stop
                                        :checked="allSelected"
                                />
                            </template>

                            <div>
                                {{ cell.header }}
                            </div>
                            <sort-column
                                    v-if="cell.sortable"
                                    :sort-field="cell.sortableField"
                                    :order-by="orderBy"
                                    @changeSortDirection="changeSortDirection"
                            />
                        </b-col>
                    </template>
                </b-row>

                <component
                        :is="hasMultiSelect ? 'b-form-checkbox-group' : 'div'"
                        v-model="multiselectModel"
                        :id="multiselectId"
                >
                <div
                        class="table-content"
                        :style="listingTableStyle"
                >
                    <draggable
                            @end="onMoveCallback"
                            v-model="appResourcesOrder"
                            v-bind="dragOptions"
                    >
                        <b-row
                                v-for="appResource in appResourceCollection.toArray()"
                                :key="appResource.uuid"
                                :class="computedRowClasses"
                                class="item-row"
                                @click.stop="rowClickCallback(appResource)"
                                :draggable="customDragAndDrop"
                                @dragstart="onDrag($event, appResource)"
                                @drop="onDrop($event, appResource)"
                                @dragover.prevent
                                @dragenter.prevent="handleDragEnter($event)"
                                @dragleave="handleDragLeave($event)"
                        >
                            <b-col
                                    v-for="(cell, index) in cells"
                                    :key="cell.header ? cell.header : `column-${index}`"
                                    :cols="cell.colSize"
                                    :sm="cellColSm(cell)"
                                    :md="cellColMd(cell)"
                                    :lg="cellColLg(cell)"
                                    class="cell"
                                    :class="cellClass(cell)"
                            >
                                <template v-if="cell.config.slot">
                                    <slot
                                            :name="cell.config.slot"
                                            :app-resource="appResource"
                                    />
                                </template>

                                <component
                                        v-if="!isSlotCell(cell)"
                                        :is="cell.vueComponent()"
                                        v-bind="cell.props()"
                                        :app-resource="appResource"
                                        @actionClick="actionClick"
                                        @optionChanged="optionChanged"
                                />
                                <slot
                                        v-else
                                        :name="cell.config.slotName"
                                        :app-resource="appResource"
                                />
                            </b-col>
                        </b-row>
                        <div
                                slot="header"
                                class="cursor"
                        >
                            {{ onDragCounter }}
                        </div>
                    </draggable>
                </div>
                </component>
            </div>
        </template>

        <b-row
                class="mt-3"
                v-if="displayPagination"
        >
            <b-col
                    :class="{'custom-pagination-wrapper' : customPagination}"
            >
                <b-pagination
                        v-if="!customPagination"
                        :per-page="pageSize"
                        :total-rows="dataTotal"
                        :align="paginationAlignment"
                        hide-goto-end-buttons
                        first-number
                        last-number
                        next-text="Next"
                        prev-text="Previous"
                        v-model="dataPage"
                        :disabled="isLoading"
                >
                    <template
                            v-if="displayDefaultPaginationWithIcons"
                            #prev-text
                    >
                        <i class="fas fa-chevron-left" />
                    </template>
                    <template
                            v-if="displayDefaultPaginationWithIcons"
                            #next-text
                    >
                        <i class="fas fa-chevron-right" />
                    </template>
                </b-pagination>

                <div v-else>
                    <b-pagination
                            :per-page="pageSize"
                            :total-rows="total"
                            :align="paginationAlignment"
                            hide-goto-end-buttons
                            first-number
                            last-number
                            :next-class="hasNext ? 'prev-next' : 'd-none'"
                            :prev-class="hasPrev ? 'prev-next' : 'd-none'"
                            ellipsis-class="page-separator d-flex align-items-center flex-column"
                            class="custom-pagination"
                            v-model="dataPage"
                            :disabled="isLoading"
                            :class="{
                                'no-next' : !hasNext,
                                'no-prev' : !hasPrev,
                            }"
                    >
                        <template slot="prev-text">
                            <i class="far fa-angle-left" />
                        </template>
                        <template slot="next-text">
                            <i class="far fa-angle-right" />
                        </template>
                    </b-pagination>

                    <div class="text-center">
                        {{ pageInfo }}
                    </div>
                </div>
            </b-col>
        </b-row>
    </b-container>
</template>

<script>
    import './ResourceListingTable.scss';
    import SortColumn from './Filters/SortColumn/SortColumn';
    import ElementLoader from '../ElementLoader/ElementLoader';
    import Draggable from 'vuedraggable';
    import {AppResourceCollection} from '@sellers-shield-home-protection/ss-core-js';
    import LoadingResourceListingTable from './LoadingResourceListingTable';
    import SlotCellLoader from './CellTypes/SlotCell/SlotCellLoader';
    import get from 'lodash/get';

    export default {
        name: 'ResourceListingTable',
        layout: 'default',

        components: {
            LoadingResourceListingTable,
            SortColumn,
            ElementLoader,
            Draggable,
        },

        props: {
            cells: {
                type: Array,
                required: true,
            },
            appResourceCollection: {
                type: Object,
                required: false,
            },
            orderBy: {
                type: Object,
                require: false,
                default: () => {
                    return {};
                },
            },
            bulkActions: {
                type: Boolean,
                required: false,
                default: false,
            },
            page: {
                type: Number,
                required: false,
                default: 1,
            },
            pageSize: {
                type: Number,
                default: 10,
            },
            total: {
                type: Number,
                required: false,
                default: 10,
            },
            paginationAlignment: {
                type: String,
                default: 'center',
            },
            isLoading: {
                type: Boolean,
                default: false,
            },
            draggable: {
                type: Boolean,
                default: false,
            },
            rowHeightEm: {
                type: Number,
                required: false,
                default: 5,
            },
            disabledMinHeight: {
                type: Boolean,
                required: false,
                default: false,
            },
            withPagination: {
                type: Boolean,
                default: true,
            },
            containerClass: {
                type: Object,
                default: function () {
                    return {};
                },
            },
            tableRowClass: {
                type: String,
                default: 'border-bottom border-left border-right py-3 d-flex align-items-center',
            },
            tableHeaderCellClass: {
                type: String,
                default: 'align-items-center cell-header py-2',
            },
            loadingTableRowClass: {
                type: String,
                default: 'border-bottom border-left border-right py-3 d-flex align-items-center',
            },
            loadingTableHeaderClass: {
                type: String,
                default: 'border-bottom d-flex align-items-center loading-cell-header',
            },
            customPagination: {
                type: Boolean,
                default: false,
            },

            rowClickCallback: {
                type: Function,
                default: (appResource) => {}
            },

            hoverable: {
                type: Boolean,
                default: false,
            },
            hasMultiSelect: {
                type: Boolean,
                default: false,
            },
            multiSelectActions: {
                type: Array,
                default: () => [],
            },
            multiSelectItems: {
                type: Array,
                default: () => [],
            },
            customDragAndDrop: {
                type: Boolean,
                default: false,
            },
            displayDefaultPaginationWithIcons: {
                type: Boolean,
                default: false,
            },
        },

        data () {
            return {
                dataPage: this.page,
                dataTotal: this.total,
                appResourcesOrder: [],
                multiselectModel: this.multiSelectItems,
                allSelected: false,
                indeterminate: false,
                multiselectId: Math.random().toString(),
                onDragCounter: 0,
            };
        },
        computed: {
            dragOptions () {
                return {
                    tag: 'div',
                    disabled: !this.draggable,
                    animation: 200,
                    dragClass: 'ghost',
                };
            },
            displayPagination () {
                return this.withPagination && this.total > this.pageSize;
            },

            hasNext() {
                const lastPage = Math.ceil(this.total / this.pageSize);
                return lastPage > this.dataPage;
            },

            hasPrev() {
                return this.dataPage > 1;
            },

            listingTableStyle () {
                return {
                    minHeight: this.disabledMinHeight ? 0 : (this.pageSize * this.rowHeightEm) + 'em',
                };
            },

            computedRowClasses() {
                let classes = this.tableRowClass;

                if (this.hoverable) {
                    classes += ' cursor-pointer';
                }

                if (this.customDragAndDrop) {
                    classes += ' custom-draggable-row';
                }

                return classes;
            },

            pageInfo () {
                return `${(this.page - 1) * this.pageSize + 1}-${this.page * this.pageSize} of ${this.total}`;
            },

            multiSelectModelCollection() {
                return this.appResourceCollection.toArray().filter(appResource =>
                    this.multiselectModel.map(value => value.uuid).includes(appResource.uuid)
                );
            },

            validMultiSelectActions() {
                return this.multiSelectActions.filter(action => !action.validateAction || (
                    typeof action.validateAction === 'function' &&
                    action.validateAction(this.multiSelectModelCollection)
                ));
            }
        },
        methods: {
            onMoveCallback () {
                this.$emit('orderAppResources', this.appResourcesOrder);
            },

            changeSortDirection (orderBy) {
                this.$emit('changeSortDirection', orderBy);
            },

            moveToPage (page) {
                this.allSelected = false;
                this.indeterminate = this.multiselectModel.length !== 0;

                this.$emit('move-to-page', page);
            },

            actionClick (event, params) {
                this.$emit('actionClick', event, params);
            },

            optionChanged (payload) {
                this.$emit('optionChanged', payload);
            },

            cellColSm(cell) {
                return get(cell.responsiveCols, 'sm', cell.colSize);
            },

            cellColMd(cell) {
                return get(cell.responsiveCols, 'md', this.cellColSm(cell));
            },

            cellColLg(cell) {
                return get(cell.responsiveCols, 'lg', this.cellColMd(cell));
            },

            cellClass(cell, displayType = 'block') {
                let cellClass = '';

                let showXs = !!cell.colSize;
                cellClass += showXs ? `d-${displayType} ` : 'd-none ';
                cellClass += this.cellColSm(cell) ? `d-sm-${displayType} ` : 'd-sm-none ';
                cellClass += this.cellColMd(cell) ? `d-md-${displayType} ` : `d-md-none d-lg-${displayType} `;

                if (cell.config.hasOwnProperty('class')){
                    if (typeof cell.config.class === "object") {
                        cellClass += this.classObjectToString(cell.config.class);
                    }
                    if (typeof cell.config.class === "string") {
                        cellClass += cell.config.class;
                    }
                }

                return cellClass;
            },

            classObjectToString(classObject){
                let classString = '';
                for (let className in classObject){
                    if (!classObject.hasOwnProperty(className)){
                        continue;
                    }
                    if (classObject[className]){
                        classString += className + ' ';
                    }
                }
                return classString;
            },

            isSlotCell(cell){
                return cell instanceof SlotCellLoader;
            },

            onMultiselectAll(checked) {
                this.allSelected = checked;

                this.appResourceCollection.each(item => {
                    let multiselectModelUuids = this.multiselectModel.map(item => item.uuid);
                    if (multiselectModelUuids.indexOf(item.uuid) === -1) {
                        this.multiselectModel.push(item);
                    }
                });

                if (checked) {
                    this.multiselectModel = this.multiselectModel.slice();
                } else {
                    this.multiselectModel = this.multiselectModel.filter((item) => {
                        return !this.appResourceCollection.has(item.uuid);
                    });
                }

                this.$emit('update:multiSelectItems', this.multiselectModel);
            },

            clearMultiSelect() {
                this.multiselectModel = [];
                this.allSelected = false;
                this.$emit('update:multiSelectItems', this.multiselectModel);
            },

            handleMultiSelectActionClick(action) {
                this.$emit('multiselect-' + action.event, this.multiselectModel);
                this.clearMultiSelect();
            },

            onDrag(event, item) {
                if (this.customDragAndDrop) {
                    this.onDragCounter = this.multiselectModel.length || 1;

                    event.dataTransfer.dropEffect = 'move';
                    event.dataTransfer.effectAllowed = 'move';
                    event.dataTransfer.setData('itemUUID', item.uuid);
                    event.dataTransfer.setDragImage(
                        document.getElementsByClassName('cursor')[0],
                        -10,
                        20
                    );
                }
            },

            onDrop(event, resource) {
                let draggableElem = event.target.closest('.custom-draggable-row');
                if (draggableElem) {
                    draggableElem.classList.remove('custom-draggable-drop-target');
                }
                this.$emit('drop', event, resource);
            },

            handleDragEnter(event) {
                if (this.customDragAndDrop) {
                    let draggableElem = event.target.closest('.custom-draggable-row');
                    if (draggableElem) {
                        draggableElem.classList.add('custom-draggable-drop-target');
                    }
                }
            },

            handleDragLeave(event) {
                let draggableElem = event.target.closest('.custom-draggable-row');
                if (draggableElem && draggableElem !== event.fromElement.closest('.custom-draggable-row')) {
                    draggableElem.classList.remove('custom-draggable-drop-target');
                }
            }
        },

        watch: {
            page: function (to, from) {
                this.dataPage = to;
            },
            dataPage: function (to, from) {
                if (this.dataPage !== this.page) {
                    this.moveToPage(this.dataPage);
                }
            },
            appResourceCollection: {
                deep: true,
                immediate: true,
                handler () {
                    if (this.appResourceCollection instanceof AppResourceCollection) {
                        this.appResourcesOrder = this.appResourceCollection.toArray();
                    }
                },
            },
            isLoading: function (to, from) {
                if (!to) {
                    this.dataTotal = this.total;
                }
            },

            multiselectModel(newValue) {
                this.$emit('update:multiSelectItems', newValue);
                if (newValue.length === 0) {
                    this.indeterminate = false;
                } else if (newValue.length === this.appResourceCollection.length) {
                    this.indeterminate = false;
                } else {
                    this.indeterminate = true;
                }
            },

            multiSelectItems(newValue) {
                this.multiselectModel = newValue;
            }
        },
    };
</script>
